import { X, Minus, Plus, Trash2, Coins, ExternalLink } from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../lib/CartContext";
import { usePirate, formatBerry } from "../lib/PirateContext";
import { formatShopifyPrice } from "../lib/shopify";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
    const {
        items,
        open,
        setOpen,
        updateQty,
        removeItem,
        subtotal,
        checkoutUrl,
        loading,
        discountCode,
        setDiscountCode,
    } = useCart();
    const { berry, spendBerry, track } = usePirate();
    const [email, setEmail] = useState("");
    const [codeInput, setCodeInput] = useState(discountCode);
    const [berryToSpend, setBerryToSpend] = useState(0);
    const [redirecting, setRedirecting] = useState(false);

    // 1000 Berry = 1 EUR
    const berryDiscountEur = berryToSpend / 1000;
    const displayTotal = Math.max(0, subtotal - berryDiscountEur);
    const maxSpendable = Math.min(
        berry,
        Math.floor(subtotal * 1000 * 0.5)
    );

    async function subscribeIfEmail() {
        if (!email || !email.includes("@")) return;
        try {
            await api.post("/newsletter", { email });
        } catch (_) {
            // ignore, non-blocking
        }
    }

    async function handleCheckout() {
        if (!checkoutUrl || items.length === 0) return;
        setRedirecting(true);
        track("checkout_click", { subtotal });
        try {
            let berryDiscountCode = null;
            // Mint a real Shopify discount for the Berry amount (if any)
            if (berryToSpend > 0) {
                if (berryToSpend > berry) {
                    toast.error("Berry non sufficienti");
                    setRedirecting(false);
                    return;
                }
                try {
                    const res = await api.post("/pirate/redeem-berry", {
                        amount_berry: berryToSpend,
                        device_id:
                            localStorage.getItem("desashop_device_id") || null,
                    });
                    berryDiscountCode = res.data?.code || null;
                    if (!berryDiscountCode) throw new Error("Codice mancante");
                    // Only decrement Berry after the discount is confirmed
                    const ok = spendBerry(berryToSpend);
                    if (!ok) {
                        toast.error("Berry non sufficienti");
                        setRedirecting(false);
                        return;
                    }
                    toast.success(
                        `Sconto di ${(berryToSpend / 1000).toFixed(2)}€ pronto al checkout!`
                    );
                } catch (err) {
                    console.error("Berry redeem failed", err);
                    const msg =
                        err?.response?.data?.detail ||
                        "Impossibile applicare lo sconto Berry ora";
                    toast.error(msg);
                    setRedirecting(false);
                    return;
                }
            }

            await subscribeIfEmail();

            // Append discount + email as URL params to Shopify checkout.
            // Prefer Berry code when redeeming; otherwise use the manual/newsletter code.
            const url = new URL(checkoutUrl);
            // If Shopify returns a checkoutUrl on our custom domain (because
            // desashop.net is still connected to the store), rewrite it to the
            // canonical myshopify domain so the browser leaves our React app
            // and lands on Shopify's real hosted checkout.
            const shopifyHost = process.env.REACT_APP_SHOPIFY_DOMAIN;
            const isOurDomain =
                url.hostname === "desashop.net" ||
                url.hostname === "www.desashop.net" ||
                url.hostname === window.location.hostname;
            if (isOurDomain && shopifyHost) {
                url.hostname = shopifyHost;
                url.port = "";
                url.protocol = "https:";
            }
            const finalCode = berryDiscountCode || codeInput;
            if (finalCode) url.searchParams.set("discount", finalCode);
            if (email) url.searchParams.set("checkout[email]", email);
            if (berryDiscountCode && codeInput) {
                toast(
                    `Ricorda di inserire manualmente il codice ${codeInput} in cassa`,
                    { duration: 6000 }
                );
            }
            window.location.href = url.toString();
        } catch (e) {
            console.error(e);
            toast.error("Errore checkout");
            setRedirecting(false);
        }
    }

    return (
        <>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-ink/60 z-40"
                        onClick={() => setOpen(false)}
                    />
                )}
            </AnimatePresence>
            <aside
                className={`fixed top-0 right-0 h-full w-full sm:w-[460px] bg-parchment border-l-2 border-ink z-50 transform transition-transform duration-300 flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`}
                data-testid="cart-drawer"
            >
                <div className="flex items-center justify-between p-5 border-b-2 border-ink bg-ink text-parchment shrink-0">
                    <div>
                        <div className="font-hand text-2xl text-gold">
                            il tuo
                        </div>
                        <div className="font-display text-3xl tracking-tight -mt-1">
                            BOTTINO
                        </div>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        className="w-10 h-10 border-2 border-parchment flex items-center justify-center hover:bg-bounty"
                        aria-label="Chiudi carrello"
                        data-testid="cart-close-btn"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {items.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="font-hand text-4xl text-bounty">
                                Vuoto!
                            </div>
                            <p className="mt-2 text-sm text-ink/70">
                                Nessun tesoro nel tuo bottino. Salpa per il
                                Grand Line.
                            </p>
                        </div>
                    ) : (
                        <AnimatePresence>
                            {items.map((it) => (
                                <motion.div
                                    key={it.lineId}
                                    layout
                                    initial={{ opacity: 0, x: 30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -30 }}
                                    className="flex gap-3 bounty-card p-3"
                                    data-testid={`cart-item-${it.lineId}`}
                                >
                                    <img
                                        src={it.image}
                                        alt={it.name}
                                        className="w-20 h-20 object-cover border-2 border-ink"
                                    />
                                    <div className="flex-1 flex flex-col">
                                        <div className="font-display text-lg leading-tight line-clamp-2">
                                            {it.name}
                                        </div>
                                        <div className="font-mono text-sm text-bounty">
                                            {formatShopifyPrice(it.price, it.currency)}
                                        </div>
                                        <div className="mt-auto flex items-center justify-between">
                                            <div className="flex items-center border-2 border-ink">
                                                <button
                                                    onClick={() =>
                                                        updateQty(
                                                            it.lineId,
                                                            it.quantity - 1
                                                        )
                                                    }
                                                    className="w-8 h-8 hover:bg-bounty hover:text-parchment"
                                                    disabled={loading}
                                                    data-testid={`cart-decrease-${it.lineId}`}
                                                >
                                                    <Minus size={12} className="mx-auto" />
                                                </button>
                                                <span className="px-3 font-mono text-sm">
                                                    {it.quantity}
                                                </span>
                                                <button
                                                    onClick={() =>
                                                        updateQty(
                                                            it.lineId,
                                                            it.quantity + 1
                                                        )
                                                    }
                                                    className="w-8 h-8 hover:bg-bounty hover:text-parchment"
                                                    disabled={loading}
                                                    data-testid={`cart-increase-${it.lineId}`}
                                                >
                                                    <Plus size={12} className="mx-auto" />
                                                </button>
                                            </div>
                                            <button
                                                onClick={() =>
                                                    removeItem(it.lineId)
                                                }
                                                disabled={loading}
                                                className="text-ink/60 hover:text-bounty"
                                                data-testid={`cart-remove-${it.lineId}`}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    )}
                </div>

                <div className="border-t-2 border-ink bg-parchment p-5 shrink-0 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="email"
                            placeholder="email@nave.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-manga text-sm"
                            data-testid="cart-email-input"
                        />
                        <input
                            type="text"
                            placeholder="Codice sconto"
                            value={codeInput}
                            onChange={(e) => {
                                setCodeInput(e.target.value.toUpperCase());
                                setDiscountCode(e.target.value.toUpperCase());
                            }}
                            className="input-manga text-sm"
                            data-testid="cart-discount-input"
                        />
                    </div>

                    {maxSpendable > 0 && (
                        <div className="border-2 border-ink bg-gold/30 p-3">
                            <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest mb-2">
                                <span className="flex items-center gap-1">
                                    <Coins size={12} /> usa Berry (max 50%)
                                </span>
                                <span
                                    className="font-bold text-bounty"
                                    data-testid="cart-berry-available"
                                >
                                    {formatBerry(berry)} disponibili
                                </span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max={maxSpendable}
                                step="100"
                                value={berryToSpend}
                                onChange={(e) =>
                                    setBerryToSpend(parseInt(e.target.value))
                                }
                                className="w-full accent-bounty"
                                data-testid="cart-berry-slider"
                            />
                            <div className="flex items-center justify-between text-xs mt-1">
                                <span className="font-mono">
                                    {formatBerry(berryToSpend)}
                                </span>
                                <span className="font-mono text-bounty font-bold">
                                    -{formatShopifyPrice(berryDiscountEur, "EUR")}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <span className="font-display text-2xl">TOTALE</span>
                        <div className="text-right">
                            {berryToSpend > 0 && (
                                <div className="font-mono text-xs line-through text-ink/50">
                                    {formatShopifyPrice(subtotal, "EUR")}
                                </div>
                            )}
                            <span
                                className="font-mono text-xl"
                                data-testid="cart-subtotal"
                            >
                                {formatShopifyPrice(displayTotal, "EUR")}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={handleCheckout}
                        disabled={
                            items.length === 0 || !checkoutUrl || redirecting
                        }
                        className="btn-manga btn-manga-red w-full text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="cart-checkout-btn"
                    >
                        {redirecting ? (
                            "Salpando..."
                        ) : (
                            <>
                                Salpa al Checkout Shopify
                                <ExternalLink size={16} className="ml-2" />
                            </>
                        )}
                    </button>
                    <p className="text-[10px] font-mono text-ink/50 text-center">
                        Checkout gestito da Shopify · Pagamenti sicuri SSL
                    </p>
                </div>
            </aside>
        </>
    );
}
